import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Badge } from "@/components/ui/baseUi/badge"
import { Text } from "@/components/ui/baseUi/text"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { ExerciseDetailsScoreCommentButton } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/components/exerciseDetailsScoreCommentButton"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { createExerciseDetailModel } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/models/exerciseDetailModel"
import { RpeTag } from "@/modules/sessions/sessionDetails/components/rpeTag"
import { useUnit } from "@/modules/utils/hooks/useUnit"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { ExerciseDoneCheckIcon } from "@images/svgIcons/exerciseDoneCheckIcon"
import { displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { ScoreDifficultyEnum } from "@modules/scores/scoreTypes"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { StarIcon, VideoIcon } from "lucide-react"
import { useMemo } from "react"

type ExerciseDetailProps = {
  exercise: SessionBlockExerciseEntity
  parentSession: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | null
  canUserCompleteExerciseScore?: boolean
  isCoach: boolean
} & React.ComponentProps<"div">

export function ExerciseDetail({
  exercise,
  parentSession,
  canUserCompleteExerciseScore = false,
  isCoach,
  className,
  ...props
}: ExerciseDetailProps) {
  const { theme } = useTheme()

  const unit = useUnit()
  const language = useLanguage()

  const {
    scoreTypeCaption,
    displayedScore,
    displayedScoreCompletionDifficulty,
    isScoreCompletionPersonalRecord,
    displayedScoreSetCompletionList,
    displayCompleteExerciseScoreOrFeedbackButton,
    shouldDisplayScoreSection,
  } = useMemo(() => createExerciseDetailModel(exercise, unit, language), [exercise, unit, language])

  return (
    <div className={cn("card flex flex-col gap-4 p-4", className)} {...props}>
      {/* Exercise card header */}
      <div
        className={`flex items-center gap-2 self-start rounded-3xl border-0 px-4 py-2 ${theme === Theme.OwnerDark ? "bg-neutral-200" : "bg-surface-background"}`}
        style={{ height: 34 }}
      >
        {parentSession && parentSession.sessionCompletedByUser && (
          <ExerciseDoneCheckIcon color="currentColor" className="mb-3 text-primary-700" />
        )}
        <div className="flex flex-col">
          <Text fontStyle="TS" className="text-textDefault-body">
            {getDisplayExerciseCategory(exercise.category, exercise.otherCategory)}
          </Text>
        </div>
      </div>

      {/* Exercise card content */}
      <div className="flex flex-col pb-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <ChronoIcon color="currentColor" className="text-textDefault-body" />
            <Text className="ml-2" fontStyle="LM">
              {exercise.chrono?.type
                ? getTrainingChronoTitle(exercise.chrono.type) + " - " + displayTotalTime(exercise.chrono)
                : word("chrono.title.no_chrono")}
            </Text>
          </div>

          {exercise.rpe !== null && exercise.rpe !== undefined && <RpeTag exercise={exercise} />}
        </div>

        <HURichTextView value={formatTextToRichText(exercise.description)} paragrapheFontStyle="BM" />

        {shouldDisplayScoreSection && (
          <div className="flex flex-col">
            <div className="mt-4 mb-2 flex w-full items-center gap-2">
              <div className="flex flex-col">
                <Text fontStyle="LM">{word("program.session.score")}</Text>
                <Text fontStyle="LXS" className="mr-2">
                  {scoreTypeCaption}
                </Text>
              </div>

              <div className="mb-1 flex-1 border-b border-neutral-100" />

              <div className="flex items-center gap-2">
                {isScoreCompletionPersonalRecord && (
                  <Badge variant="primaryIntense" size="md" className="pr-2 italic">
                    <StarIcon className="currentColor" />
                    {word("scoreboard.personalRecord.shortLabel")}
                  </Badge>
                )}

                {displayedScoreCompletionDifficulty && (
                  <Badge
                    variant={
                      exercise.score?.difficulty === ScoreDifficultyEnum.RX ? "primaryIntense" : "neutralIntense"
                    }
                    size="md"
                    className="italic"
                  >
                    {displayedScoreCompletionDifficulty}
                  </Badge>
                )}

                <Text fontStyle="LL" className="mr-2 font-bold">
                  {displayedScore}
                </Text>
              </div>
            </div>

            {displayedScoreSetCompletionList && displayedScoreSetCompletionList.length > 1 && (
              <div className="grid grid-cols-3 justify-between gap-2">
                {displayedScoreSetCompletionList.map((scoreSetCompletion, index) => (
                  <Text fontStyle="BXS" key={index} className="">
                    {index + 1}. {scoreSetCompletion}
                  </Text>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {exercise.requiredMedia && (
        <div className="my-2 flex flex-row items-center justify-start gap-2">
          <VideoIcon color="currentColor" className="text-textError" />
          <Text fontStyle="BS">
            {isCoach
              ? word("program.planning.create.exercise.media_requirement.required")
              : word("program.planning.create.exercise.media_requirement.requested")}
          </Text>
        </div>
      )}

      {/* Exercise card footer*/}
      {canUserCompleteExerciseScore && !!parentSession && displayCompleteExerciseScoreOrFeedbackButton && (
        <ExerciseDetailsScoreCommentButton parentSession={parentSession} exercise={exercise} />
      )}
    </div>
  )
}
